import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../../lib/api";

const Pill = ({ ok, label }) => (
  <span
    className={`inline-flex items-center gap-2 px-3 py-1.5 border ${
      ok ? "border-emerald-400/40 text-emerald-300" : "border-yellow-400/40 text-yellow-300"
    } font-accent text-[10px] tracking-[0.24em] uppercase`}
  >
    <span
      className={`w-1.5 h-1.5 rounded-full ${ok ? "bg-emerald-400" : "bg-yellow-400"}`}
    />
    {label}
  </span>
);

const NotificationsPanel = () => {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(null);
  const [lastResult, setLastResult] = useState(null);
  const [showSetup, setShowSetup] = useState(false);

  const load = () =>
    api
      .get("/admin/notifications/config")
      .then(({ data }) => setCfg(data))
      .catch(() => setCfg({ email_enabled: false, whatsapp_enabled: false }));

  useEffect(() => {
    load();
  }, []);

  const sendTest = async (channel) => {
    setBusy(channel);
    setLastResult(null);
    try {
      const { data } = await api.post(
        `/admin/notifications/test?channel=${channel}`
      );
      setLastResult(data);
      const summary = [];
      if (data.email) {
        summary.push(
          data.email.sent
            ? `✓ Email → ${data.email.to}`
            : `✗ Email failed (${data.email.error || "unknown"})`
        );
      }
      if (data.whatsapp) {
        summary.push(
          data.whatsapp.sent
            ? `✓ WhatsApp → +${data.whatsapp.to}`
            : `✗ WhatsApp failed (${data.whatsapp.error || "unknown"})`
        );
      }
      const ok =
        (data.email?.sent ?? true) && (data.whatsapp?.sent ?? true);
      (ok ? toast.success : toast.error)(summary.join("\n"));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Test failed");
    } finally {
      setBusy(null);
    }
  };

  if (!cfg) {
    return <div className="text-white/40 text-sm">Loading notifications…</div>;
  }

  return (
    <div
      data-testid="notifications-panel"
      className="bg-onyx border border-white/10 p-8 space-y-6"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="font-accent text-[10px] tracking-[0.28em] uppercase text-gold mb-2">
            Order Notifications
          </div>
          <div className="font-heading text-2xl text-ivory">
            Email &amp; WhatsApp alerts
          </div>
          <p className="text-white/50 text-sm mt-2 max-w-xl">
            Every new order — Stripe, PayPal or Bank Transfer — automatically
            sends an alert to the addresses below. Test it now to make sure
            everything is wired up correctly.
          </p>
        </div>
        <div className="flex flex-col gap-2 items-end">
          <Pill ok={cfg.email_enabled} label={cfg.email_enabled ? "Email ready" : "Email off"} />
          <Pill ok={cfg.whatsapp_enabled} label={cfg.whatsapp_enabled ? "WhatsApp ready" : "WhatsApp off"} />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="border border-white/10 p-5">
          <div className="font-accent text-[10px] tracking-[0.24em] uppercase text-gold mb-2">
            Email
          </div>
          <div className="text-ivory text-sm mb-1">{cfg.admin_email || "—"}</div>
          <div className="text-white/40 text-xs">
            Sent from: {cfg.email_from || "—"}
          </div>
          <button
            data-testid="notify-test-email"
            onClick={() => sendTest("email")}
            disabled={busy === "email" || !cfg.email_enabled}
            className="mt-4 w-full font-accent text-[10px] tracking-[0.28em] uppercase border border-gold text-gold py-2.5 hover:bg-gold hover:text-black disabled:opacity-30 disabled:cursor-not-allowed"
          >
            {busy === "email" ? "Sending…" : "Send test email"}
          </button>
        </div>

        <div className="border border-white/10 p-5">
          <div className="font-accent text-[10px] tracking-[0.24em] uppercase text-gold mb-2">
            WhatsApp (CallMeBot)
          </div>
          <div className="text-ivory text-sm mb-1">
            {cfg.whatsapp_phone ? `+${cfg.whatsapp_phone}` : "Not configured"}
          </div>
          <div className="text-white/40 text-xs">
            {cfg.whatsapp_enabled ? "Active" : "Add CallMeBot key to enable"}
          </div>
          <div className="mt-4 flex gap-2">
            <button
              data-testid="notify-test-whatsapp"
              onClick={() => sendTest("whatsapp")}
              disabled={busy === "whatsapp" || !cfg.whatsapp_enabled}
              className="flex-1 font-accent text-[10px] tracking-[0.28em] uppercase border border-gold text-gold py-2.5 hover:bg-gold hover:text-black disabled:opacity-30 disabled:cursor-not-allowed"
            >
              {busy === "whatsapp" ? "Sending…" : "Send test WhatsApp"}
            </button>
            <button
              data-testid="notify-setup-toggle"
              onClick={() => setShowSetup((v) => !v)}
              className="font-accent text-[10px] tracking-[0.28em] uppercase border border-white/20 px-3 py-2.5 text-white/70 hover:text-white"
            >
              {showSetup ? "Hide setup" : "Setup"}
            </button>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          data-testid="notify-test-all"
          onClick={() => sendTest("all")}
          disabled={busy === "all"}
          className="font-accent text-[10px] tracking-[0.28em] uppercase bg-gold text-black px-6 py-2.5 hover:bg-champagne disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {busy === "all" ? "Sending…" : "Send test to both channels"}
        </button>
      </div>

      {lastResult && (
        <pre className="bg-black/40 border border-white/10 p-4 text-xs text-white/70 overflow-x-auto">
          {JSON.stringify(lastResult, null, 2)}
        </pre>
      )}

      {showSetup && (
        <div className="border border-gold/30 bg-gold/5 p-5 text-sm text-white/80 space-y-3">
          <div className="font-accent text-[11px] tracking-[0.28em] uppercase text-gold">
            One-time WhatsApp setup (CallMeBot · free)
          </div>
          <ol className="list-decimal list-inside space-y-1.5 text-white/70">
            <li>
              On the phone you want alerts sent to, add{" "}
              <span className="text-gold">+34 644 51 19 96</span> as a contact
              (verify the current number on{" "}
              <a
                href="https://www.callmebot.com/blog/free-api-whatsapp-messages/"
                target="_blank"
                rel="noreferrer"
                className="text-gold underline"
              >
                callmebot.com
              </a>
              ).
            </li>
            <li>
              From that phone, send the bot this exact message via WhatsApp:
              <pre className="bg-black/40 mt-1 p-2 text-xs">
                I allow callmebot to send me messages
              </pre>
            </li>
            <li>You will receive your personal <span className="text-gold">API key</span> via WhatsApp.</li>
            <li>
              Add these three lines to <code className="text-gold">backend/.env</code> and redeploy:
              <pre className="bg-black/40 mt-1 p-2 text-xs">
{`CALLMEBOT_ENABLED="true"
CALLMEBOT_PHONE="6287781058814"   # your number with country code, no +
CALLMEBOT_API_KEY="123456"         # the key the bot sent you`}
              </pre>
            </li>
            <li>
              Come back here and press <em>Send test WhatsApp</em>. ✨
            </li>
          </ol>
          <p className="text-white/50 text-xs">
            CallMeBot is free for personal use. For business-grade SLA, switch
            to Twilio WhatsApp Business later — the same{" "}
            <code className="text-gold">whatsapp_service.py</code> swap-point
            handles it.
          </p>
        </div>
      )}
    </div>
  );
};

export default NotificationsPanel;
